import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import axios from '../utils/api';

const trimTrailingSlash = (value) => value.replace(/\/+$/, '');

const getOrCreateMeta = (attribute, key) => {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  return element;
};

const setMeta = (attribute, key, content) => {
  const element = getOrCreateMeta(attribute, key);
  element.setAttribute('content', content);
};

const getOrCreateCanonical = () => {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  return element;
};

const getOrCreateLink = (rel) => {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  return element;
};

const toAbsoluteUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) return `${trimTrailingSlash(axios.defaults.baseURL || window.location.origin)}${url}`;
  return url;
};

export default function SeoManager({
  title,
  description,
  keywords,
  author,
  image,
  robots,
  type,
  canonicalPath
}) {
  const location = useLocation();
  const {
    brandName,
    seoTitle,
    seoDescription,
    seoKeywords,
    seoAuthor,
    seoImage,
    seoCanonicalUrl,
    seoRobots,
    siteFavicon,
    appleTouchIcon
  } = useSettings();

  useEffect(() => {
    const pageTitle = title || seoTitle || brandName || "Guraba's Choice";
    const pageDescription = description || seoDescription || '';
    const pageKeywords = keywords || seoKeywords || '';
    const pageAuthor = author || seoAuthor || '';
    const pageRobots = robots || seoRobots || 'index, follow';
    const pageImage = toAbsoluteUrl(image || seoImage);
    const favicon = toAbsoluteUrl(siteFavicon);
    const touchIcon = toAbsoluteUrl(appleTouchIcon);
    const canonicalBase = seoCanonicalUrl
      ? trimTrailingSlash(seoCanonicalUrl)
      : trimTrailingSlash(window.location.origin);
    const canonicalUrl = `${canonicalBase}${canonicalPath || location.pathname}`;

    document.title = pageTitle;
    setMeta('name', 'robots', pageRobots);
    setMeta('name', 'description', pageDescription);
    setMeta('name', 'keywords', pageKeywords);
    setMeta('name', 'author', pageAuthor);
    setMeta('property', 'og:title', pageTitle);
    setMeta('property', 'og:description', pageDescription);
    setMeta('property', 'og:image', pageImage);
    setMeta('property', 'og:type', type || 'website');
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('name', 'twitter:card', pageImage ? 'summary_large_image' : 'summary');
    setMeta('name', 'twitter:title', pageTitle);
    setMeta('name', 'twitter:description', pageDescription);
    setMeta('name', 'twitter:image', pageImage);
    getOrCreateCanonical().setAttribute('href', canonicalUrl);

    if (favicon) {
      const faviconLink = getOrCreateLink('icon');
      faviconLink.setAttribute('href', favicon);
      faviconLink.setAttribute('sizes', '32x32');
    }
    if (touchIcon) {
      getOrCreateLink('apple-touch-icon').setAttribute('href', touchIcon);
    }
  }, [
    appleTouchIcon,
    author,
    brandName,
    canonicalPath,
    description,
    image,
    keywords,
    location.pathname,
    robots,
    seoAuthor,
    seoCanonicalUrl,
    seoDescription,
    seoImage,
    seoKeywords,
    seoRobots,
    seoTitle,
    siteFavicon,
    title,
    type
  ]);

  return null;
}
